import React from "react";
import {
  Container,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Typography,
  Box,
} from "@mui/material";
import { motion } from "framer-motion";
import Cards from "./Cards";
import "./Home.css";
import InfoPopup from "./InfoPopup";
import { useNavigate } from "react-router-dom";
import { analytics } from "../firebase/firebaseClient";
import { logEvent } from "firebase/analytics";

const dashboards = [
  {
    id: 1,
    name: "Sales Dashboard",
    img: "/Gemini_Generated_Image_b2c5g6b2c5g6b2c5.png",
    link: "https://sales-insights.haqdarshak.com",
    info: "Track citizens impacted, applications submitted, MSMEs reached, HDs trained, and benefits unlocked with filters by project, HD, or citizen details",
  },
  {
    id: 2,
    name: "Project 360 Dashboard",
    img: "/Gemini_Generated_Image_ws6zixws6zixws6z.png",
    link: "https://project360.insights.haqdarshak.com",
    info: "Explore project milestones, KPIs, and high-level summaries across all active projects.",
  },
  {
    id: 3,
    name: "Achievement Dashboard",
    img: "/Gemini_Generated_Image_wdol4gwdol4gwdol.png",
    link: "https://usersreport-insights.haqdarshak.com",
    info: "View daily, weekly, and monthly performance by PID, organization, date, or manager. Drill down to individual-level data",
  },
  {
    id: 4,
    name: "DFL Dashboard",
    img: "/Gemini_Generated_Image_1m8kuq1m8kuq1m8k.png",
    link: "https://dfl-insights.haqdarshak.com",
    info: "Get an overview of camps, institutes, trainers, and participants under the DFL initiative.",
  },
  {
    id: 5,
    name: "Voice of Citizens",
    img: "/Gemini_Generated_Image_aw1fesaw1fesaw1f.png", // replace with your actual image
    internalRoute: "/dashboard",
    info: "Explore Dashboard inside this."
  },
  {
    id: 6,
    name: "Sales-Impact Dashboard",
    img: "/Gemini_Generated_Image_ivwdz8ivwdz8ivwd.png", // replace with your actual image
    link: "https://sales-impact.haqdarshak.com/",
    info: "Analyze the sales impact and performance metrics across different regions and timeframes.This dashboard can also be shared externally.",
  },
];

const additionalImages = [
  {
    img: "/a.png",
    text: "5000+ Welfare Schemes",
    extraText:
      "Our platform provides access to over 5,000 central, state, and local welfare schemes...",
  },
  {
    img: "/5.png",
    text: "Haqdarshak Agents",
    extraText:
      "More than 51,000 Haqdarshak field agents are trained and deployed across India...",
  },
  {
    img: "/3.png",
    text: "INR 18,636 Cr+ Benefits Unlocked",
    extraText:
      "We’ve facilitated the unlocking of benefits worth over INR 18,636 crore,...",
  },
  {
    img: "/4.png",
    text: "Yojana Kendra",
    extraText:
      "Yojana Kendras are community-run service centers providing end-to-end support...",
  },
];

const Home = () => {
  const navigate = useNavigate();

  return (
    <Box className="home-container">
      <Container maxWidth="lg" sx={{ textAlign: "center", color: "white", paddingTop: "50px" }}>
        <Typography variant="h2" sx={{ fontWeight: "bold", mb: 2 }}>
          Welcome to Haqdarshak Insights
        </Typography>
        <Typography variant="h5" sx={{ mb: 5 }}>
          Empowering communities with data-driven insights
        </Typography>
      </Container>

      <Cards />

      {/* Highlights Section Header */}
      <Container maxWidth="lg" sx={{ textAlign: "center", mt: 5 }}>
        <Typography variant="h3" sx={{ fontWeight: "bold", color: "white", mb: 3 }}>
          Our Key Highlights
        </Typography>
        <Typography variant="subtitle1" sx={{ color: "white", mb: 4 }}>
          Discover the impact we're making through welfare schemes, agents, and services
        </Typography>
      </Container>

      {/* Flip Card Grid */}
      <Grid container spacing={4} justifyContent="center">
        {additionalImages.map((item, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <motion.div whileHover={{ scale: 1.1 }} className="flip-card">
              <Box className="flip-card-inner">
                <Box
                  className="flip-card-front"
                  sx={{ backgroundImage: `url(${process.env.PUBLIC_URL}${item.img})` }}
                >
                  <Typography className="image-text">{item.text}</Typography>
                </Box>
                <Box className="flip-card-back">
                  <Typography>{item.extraText}</Typography>
                </Box>
              </Box>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* Info Popup Section */}
      <Container maxWidth="lg" sx={{ textAlign: "center", mt: 5 }}>
        <InfoPopup />
      </Container>

      {/* Dashboards Section */}
      <Container maxWidth="lg" sx={{ textAlign: "center", mt: 5 }}>
        <Typography variant="h3" sx={{ fontWeight: "bold", color: "white", mb: 3 }}>
          Explore Our Dashboards
        </Typography>
        <Grid container spacing={8} justifyContent="center">
          {dashboards.map((dash) => (
            <Grid item xs={12} sm={3} md={3} key={dash.id}>
              <motion.div whileHover={{ scale: 1.05 }} className="dashboard-card-hover">
                <CardActionArea
                  onClick={() => {
                    if (analytics) {
                      logEvent(analytics, "dashboard_clicked", {
                        dashboard_name: dash.name,
                        dashboard_id: dash.id,
                        time: new Date().toISOString()
                      });
                    }
                  
                    if (dash.link) {
                      window.open(dash.link, "_blank");
                    } else if (dash.internalRoute) {
                      navigate(dash.internalRoute);
                    }
                  }}
                  sx={{
                    cursor: dash.link || dash.internalRoute ? "pointer" : "default",
                    pointerEvents: dash.link || dash.internalRoute ? "auto" : "none",
                  }}
                >
                  <Card
                    className="dashboard-hover-card"
                    sx={{
                      height: 243,
                      width: "120%",
                      maxWidth: 300,
                      display: "flex",
                      flexDirection: "column",
                      opacity: dash.link || dash.internalRoute ? 1 : 0.6,
                      margin: "0 auto",
                    }}
                  >
                    <Box className="image-container">
                      <CardMedia
                        component="img"
                        image={`${process.env.PUBLIC_URL}${dash.img}`}
                        alt={dash.name}
                        className="dashboard-img"
                        sx={{ width: "100%", height: "auto" }}
                      />
                      <Box className="hover-info">
                        <Typography variant="body2">{dash.info}</Typography>
                      </Box>
                    </Box>
                    <CardContent sx={{ backgroundColor: "rgba(0, 0, 0, 0.7)", color: "white" }}>
                      <Typography
                        variant="body1"
                        align="center"
                        sx={{ fontSize: "14px", fontWeight: "bold" }}
                      >
                        {dash.name}
                      </Typography>
                    </CardContent>
                  </Card>
                </CardActionArea>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Home;
